import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ZoomIn, ZoomOut, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF worker - use jsdelivr CDN with proper ES module support
pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs';

interface DocumentViewerProps {
  filePath: string;
  fileName: string;
  onLoadStart?: () => void;
  maxHeight?: string;
}

type DocumentType = 'pdf' | 'image' | 'unsupported';

const DocumentViewer: React.FC<DocumentViewerProps> = ({ filePath, fileName, onLoadStart, maxHeight = '600px' }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Call onLoadStart when component mounts to fetch presigned URL if needed
  React.useEffect(() => {
    onLoadStart?.();
  }, [onLoadStart, filePath]);

  // Detect document type from file extension
  const getDocumentType = (): DocumentType => {
    const extension = fileName.toLowerCase().split('.').pop();
    if (extension === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) return 'image';
    return 'unsupported';
  };

  const documentType = getDocumentType();

  const handleDocumentLoadSuccess = (pdf: any) => {
    setNumPages(pdf.numPages);
    setLoading(false);
    setError(null);
  };

  const handleDocumentLoadError = (error: any) => {
    console.error('Error loading document:', error);
    setError('Failed to load document. Please try downloading instead.');
    setLoading(false);
  };

  const handleImageLoadError = () => {
    setError('Failed to load image. Please try downloading instead.');
    setLoading(false);
  };

  const handleImageLoad = () => {
    setLoading(false);
    setError(null);
  };

  const goToPreviousPage = () => {
    setPageNumber(Math.max(pageNumber - 1, 1));
  };

  const goToNextPage = () => {
    if (numPages) {
      setPageNumber(Math.min(pageNumber + 1, numPages));
    }
  };

  const zoomIn = () => {
    setScale(Math.min(scale + 0.2, 2));
  };

  const zoomOut = () => {
    setScale(Math.max(scale - 0.2, 0.5));
  };

  // Unsupported file type
  if (documentType === 'unsupported') {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          File type not supported for preview. Please download the file to view it.
        </AlertDescription>
      </Alert>
    );
  }

  // Image viewer
  if (documentType === 'image') {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex gap-2 justify-center border-b pb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground px-2 py-1">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 2}
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScale(1)}
          >
            Reset
          </Button>
        </div>

        <div className="border rounded-lg bg-gray-50 overflow-auto" style={{ maxHeight: maxHeight }}>
          {loading && (
            <div className="flex items-center justify-center p-4" style={{ minHeight: maxHeight }}>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          )}
          {error && (
            <div className="flex items-center justify-center p-4" style={{ minHeight: maxHeight }}>
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
          {!error && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem' }}>
              <img
                src={filePath}
                alt={fileName}
                style={{ transform: `scale(${scale})` }}
                onLoad={handleImageLoad}
                onError={handleImageLoadError}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  // PDF viewer
  return (
    <div className="flex flex-col gap-4">
      {/* Controls */}
      <div className="flex gap-2 flex-wrap items-center border-b pb-4">
        {/* Zoom Controls */}
        <Button
          variant="outline"
          size="sm"
          onClick={zoomOut}
          disabled={scale <= 0.5}
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground px-2 py-1 min-w-[60px]">
          {Math.round(scale * 100)}%
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={zoomIn}
          disabled={scale >= 2}
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setScale(1)}
        >
          Reset
        </Button>

        {/* Divider */}
        <div className="h-6 border-l border-gray-300 mx-2" />

        {/* Page Controls */}
        <Button
          variant="outline"
          size="sm"
          onClick={goToPreviousPage}
          disabled={pageNumber <= 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground px-2 py-1">
          Page {pageNumber} {numPages && `of ${numPages}`}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNextPage}
          disabled={!numPages || pageNumber >= numPages}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Document View */}
      <div className="border rounded-lg bg-gray-50 p-4 overflow-auto" style={{ maxHeight: maxHeight, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        {loading && (
          <div className="flex items-center justify-center h-96">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!error && (
          <Document
            file={filePath}
            onLoadSuccess={handleDocumentLoadSuccess}
            onLoadError={handleDocumentLoadError}
            loading={null}
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              renderTextLayer={true}
              renderAnnotationLayer={true}
              loading={null}
            />
          </Document>
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
